import { Card } from "@mui/material";
import { useCallback } from "react";
import { Outlet, useNavigate, useParams, useSearchParams } from "react-router";

import { MapEditorFeatureHoverProvider } from "~/modules/mapEditor/hooks/useMapEditorFeatureHover";

import useCreateProjectUrl from "../../hooks/useCreateProjectUrl";
import { useMapEditorFilterParams } from "../../hooks/useMapEditorFilterParams";
import { useMapFilters } from "../../hooks/useMapFilters";
import useRequireCurrentProject from "../../hooks/useRequireCurrentProject";
import useProjectMapEditorScreenData from "./api/useProjectMapEditorScreenData";
import { ProjectMapEditorLeftPanel } from "./components/ProjectMapEditorLeftPanel";
import { ProjectMapEditorMapView } from "./components/ProjectMapEditorMapView";

export default function ProjectMapEditorScreen() {
    const { project } = useRequireCurrentProject();
    const { layerId, mapEditorFeatureId } = useParams();
    const [searchParams] = useSearchParams();
    const createProjectUrl = useCreateProjectUrl();
    const navigate = useNavigate();

    const { filters, setFilter } = useMapFilters();
    const mapFiltersToQueryParams = useMapEditorFilterParams();

    const { allLayerData, filterOptions, functionTypes, observationTypes, projectData, researchTypes, layers } =
        useProjectMapEditorScreenData(project.id, mapFiltersToQueryParams);

    const handleSelect = useCallback(
        (layerId?: string, featureId?: string) => {
            searchParams.set("page", String(0));

            navigate({
                pathname: createProjectUrl(["map-editor", layerId, featureId].filter(Boolean).join("/")),
                search: layerId ? searchParams.toString() : undefined,
            });
        },
        [createProjectUrl, navigate, searchParams],
    );

    return (
        <MapEditorFeatureHoverProvider>
            <Card sx={{ height: "100%", width: "100%" }}>
                <ProjectMapEditorMapView
                    projectId={project.id}
                    selectedFeatureId={mapEditorFeatureId}
                    selectedLayerId={layerId}
                    onSelect={handleSelect}
                    allLayerData={allLayerData}
                    rightPanel={<Outlet />}
                    leftPanel={
                        <ProjectMapEditorLeftPanel
                            project={project}
                            filterOptions={filterOptions}
                            setFilter={setFilter}
                            filters={filters}
                            onSelect={handleSelect}
                            selectedLayerId={layerId}
                            functionTypeTreeData={functionTypes}
                            observationTypeTreeData={observationTypes}
                            protocolTreeData={projectData}
                            researchTypeData={researchTypes}
                            layersData={layers}
                        />
                    }
                />
            </Card>
        </MapEditorFeatureHoverProvider>
    );
}
